// Display-only accessories attached to the original Rev4 coordinate frame.
// USB: build_revision4.py:135 and pcb_reference_rev25.json:43–64 (J13).
// J15 is a servo header, NOT the USB-C-shaped custom BLDC connection.
// Sensor: original BRep shaft axis (-2.9384, -22.4555), Z35..110; canal entry Z132.
// No replacement sensor, CAD export, wiring pinout or physical fit approval.
export const measurementConnectionGeometry = Object.freeze({
  pcbName: "REFERENCE_REV25_BLDC_PCB_ENVELOPE_28x46_1x1_6_part",
  sensorName: "REFERENCE_SENSOR_SHAFT_D12_part",
  usbMouthCad: Object.freeze([-20.49255781506445, 24.29001804081161, 79.9]),
  usbOutwardCad: Object.freeze([-.6084147481523228, .793619237563421, 0]),
  sensorTopCad: Object.freeze([-2.9384, -22.4555, 110]),
  sensorExitCad: Object.freeze([-2.9384, -22.4555, 132]),
  apertureWidthMm: 4.4, apertureHeightMm: 10.4,
  plugWidthMm: 3.8, plugHeightMm: 9.6, plugRearMm: 26
});

export function createMeasurementConnections(THREE, assembly, { mesh, material } = {}) {
  if (typeof mesh !== "function" || typeof material !== "function") throw new TypeError("Registered mesh and material factories required");
  const geometry = measurementConnectionGeometry;
  const component = name => {
    const matches = assembly?.components?.filter(item => item.name === name);
    if (matches?.length !== 1 || !matches[0].object?.isObject3D) throw new TypeError(`Missing unique original CAD component: ${name}`);
    return matches[0].object;
  };
  const pcb = component(geometry.pcbName), sensor = component(geometry.sensorName);
  if (pcb.getObjectByName("measurement-custom-usb-c-plug") || sensor.getObjectByName("measurement-rh-t-cable-anchor")) throw new Error("Measurement connections already attached");
  const dark = material(0x242a29, { roughness: .85, metalness: .015 });
  const metal = material(0xaebbbd, { roughness: .34, metalness: .72 });
  const usbPlugGroup = new THREE.Group(); usbPlugGroup.name = "measurement-custom-usb-c-plug";
  usbPlugGroup.position.set(...geometry.usbMouthCad);
  // Right-handed local axes: -U (narrow width), CAD +Z (height), +N (plug out).
  usbPlugGroup.quaternion.setFromRotationMatrix(new THREE.Matrix4().makeBasis(
    new THREE.Vector3(-.793619237563421, -.6084147481523228, 0),
    new THREE.Vector3(0, 0, 1), new THREE.Vector3(...geometry.usbOutwardCad)
  ));
  usbPlugGroup.userData = { schematic: true, reference: "J13", purpose: "custom-bldc-connection", standardUsbHostCompatible: false, dimensionsEvidence: "illustrative-plug-within-nominal-aperture" };
  pcb.add(usbPlugGroup);
  const roundedProfile = (width, height, radius) => {
    const shape = new THREE.Shape(), x = -width / 2, y = -height / 2;
    shape.moveTo(x + radius, y); shape.lineTo(x + width - radius, y);
    shape.quadraticCurveTo(x + width, y, x + width, y + radius);
    shape.lineTo(x + width, y + height - radius);
    shape.quadraticCurveTo(x + width, y + height, x + width - radius, y + height);
    shape.lineTo(x + radius, y + height);
    shape.quadraticCurveTo(x, y + height, x, y + height - radius);
    shape.lineTo(x, y + radius); shape.quadraticCurveTo(x, y, x + radius, y);
    shape.closePath(); return shape;
  };
  const extruded = (name, profile, from, depth, surface) => {
    const shape = new THREE.ExtrudeGeometry(profile, { depth, bevelEnabled: false, steps: 1, curveSegments: 8 });
    const object = mesh(shape, surface, usbPlugGroup, [0, 0, from]); object.name = name; return object;
  };
  // The shell passes through the existing vertical 4.4 x 10.4-mm aperture.
  // The wider grip begins outside the hood, not inside its opening.
  const shell = roundedProfile(geometry.plugWidthMm, geometry.plugHeightMm, 1.8);
  shell.holes.push(new THREE.Path(roundedProfile(3.2, 9, 1.5).getPoints(8).reverse()));
  const plugShell = extruded("measurement-usb-c-inserted-shell", shell, -1.2, 6, metal);
  const plugGrip = extruded("measurement-usb-c-exterior-grip", roundedProfile(5.8, 11.8, 2.4), 4.5, 13.5, dark);
  const strainRelief = mesh(new THREE.CylinderGeometry(1.5, 2.1, 8, 20), dark, usbPlugGroup, [0, 0, 22]);
  strainRelief.name = "measurement-usb-c-strain-relief"; strainRelief.rotation.x = Math.PI / 2;
  const usbAnchor = new THREE.Object3D(); usbAnchor.name = "measurement-bldc-cable-anchor";
  usbAnchor.position.z = geometry.plugRearMm; usbPlugGroup.add(usbAnchor);
  const sensorAnchor = new THREE.Object3D(); sensorAnchor.name = "measurement-rh-t-cable-anchor";
  sensorAnchor.position.set(...geometry.sensorTopCad); sensorAnchor.userData = { purpose: "rh-t-sensor-cable", originalSensorReference: true };
  const sensorExit = new THREE.Object3D(); sensorExit.name = "measurement-rh-t-canal-exit";
  sensorExit.position.set(...geometry.sensorExitCad); sensorExit.userData = { purpose: "rh-t-cable-through-canal", schematicCable: true };
  sensor.add(sensorAnchor, sensorExit);
  assembly.root.updateMatrixWorld(true);
  // Owning scene factories register all resources. No material/geometry on the
  // original CAD objects is changed, and this module has no independent dispose.
  return { usbPlugGroup, usbAnchor, sensorAnchor, sensorExit, plugShell, plugGrip, strainRelief };
}
